import { getChartTitle } from '@/shared/copy/charts';
import { getErrorCopy } from '@/shared/copy/errors';
import { uiTokens } from '@/theme/uiTokens';
import { StatusBanner } from '@/ui';
import { Box } from '@/ui/primitives';
import { chartLoadErrorBannerSx } from './chartLoadErrorBannerSx';
import { MetricChartPanel } from './MetricChartPanel';
import { MiniMetricChartHeader } from './MiniMetricChartHeader';
import { MiniMetricLineChart } from './MiniMetricLineChart';
import { type StackedMetricsMiniPanelsLayout } from './StackedMetricsStatusChartSection.layout';
import {
  renderMiniMetricEmptyState,
  renderMiniMetricHeaderLoadingState,
  renderMiniMetricLoadingState,
} from './StackedMetricsStatusChartSection.states';
import type { MetricsStatusChartPanelDisplayModel } from './useMetricsStatusChartPanelModel';
import type { ReactElement } from 'react';

type MiniMetricPanelsProps = {
  loading: boolean;
  showPlaceholder?: boolean;
  model: MetricsStatusChartPanelDisplayModel;
  miniPanels: StackedMetricsMiniPanelsLayout;
  miniMetricEmptyTitle: string;
  showFlakeScore?: boolean;
  hasMetricChartValue: {
    passRate: boolean;
    frequency: boolean;
    flakeScore: boolean;
    duration: boolean;
  };
};

export const MiniMetricPanels = ({
  loading,
  showPlaceholder = false,
  model,
  miniPanels,
  miniMetricEmptyTitle,
  showFlakeScore = true,
  hasMetricChartValue,
}: MiniMetricPanelsProps): ReactElement => {
  const passRateHasSinglePoint = (model.passRateChartData?.length ?? 0) === 1;
  const frequencyHasSinglePoint = (model.frequencyChartData?.length ?? 0) === 1;
  const flakeScoreHasSinglePoint = (model.flakeScoreChartData?.length ?? 0) === 1;
  const durationHasSinglePoint = (model.durationChartData?.length ?? 0) === 1;
  const chartErrorNode = (
    <Box
      sx={{
        height: miniPanels.chartHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <StatusBanner
        tone="red"
        variant="outlined"
        message={getErrorCopy('banner.chartLoadFailed')}
        sx={chartLoadErrorBannerSx}
      />
    </Box>
  );

  const metricChart = (summary: ReactElement, chartNode: ReactElement): ReactElement => (
    <MetricChartPanel
      header={summary}
      height={miniPanels.height}
      headerHeight={miniPanels.headerHeight}
      panelPaddingTop={miniPanels.panelPaddingTop}
      panelPaddingBottom={miniPanels.panelPaddingBottom}
      headerPaddingX={miniPanels.headerPaddingX}
      headerPaddingTop={miniPanels.headerPaddingTop}
      headerPaddingBottom={miniPanels.headerPaddingBottom}
      contentPaddingX={miniPanels.contentPaddingX}
      contentPaddingTop={miniPanels.contentPaddingTop}
      contentPaddingBottom={miniPanels.contentPaddingBottom}
    >
      <Box
        sx={{
          px: miniPanels.chartWrapperPaddingX,
          pt: miniPanels.chartWrapperPaddingTop,
          pb: miniPanels.chartWrapperPaddingBottom,
        }}
      >
        {chartNode}
      </Box>
    </MetricChartPanel>
  );

  return (
    <Box
      sx={(muiTheme) => ({
        display: 'grid',
        gridTemplateColumns: showFlakeScore
          ? 'repeat(4, minmax(0, 1fr))'
          : 'repeat(3, minmax(0, 1fr))',
        columnGap: miniPanels.columnGap,
        rowGap: miniPanels.rowGap,
        width: '100%',
        minWidth: 0,
        [muiTheme.breakpoints.down(760)]: {
          gridTemplateColumns: 'minmax(0, 1fr)',
        },
        [muiTheme.breakpoints.up('lg')]: {
          gridTemplateColumns: 'minmax(0, 1fr)',
        },
      })}
    >
      {metricChart(
        showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: miniPanels.headerHeight }} />
        ) : loading ? (
          renderMiniMetricHeaderLoadingState()
        ) : (
          <MiniMetricChartHeader
            title={getChartTitle('passRate')}
            summary={hasMetricChartValue.passRate ? model.passRateSummary : null}
          />
        ),
        showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: miniPanels.chartHeight }} />
        ) : loading ? (
          renderMiniMetricLoadingState(miniPanels.chartHeight)
        ) : model.showChartErrorState ? (
          chartErrorNode
        ) : model.passRateMiniChartData && hasMetricChartValue.passRate ? (
          <MiniMetricLineChart
            data={model.passRateMiniChartData}
            metricKey="passRate"
            color={uiTokens.statusColors.green}
            height={miniPanels.chartHeight}
            marginLeft={miniPanels.chartMargins.left}
            marginRight={miniPanels.chartMargins.right}
            marginTop={miniPanels.chartMargins.top}
            marginBottom={miniPanels.chartMargins.bottom}
            yAxisWidth={miniPanels.yAxisWidth}
            axisStrokeWidth={miniPanels.axisStrokeWidth}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            yDomainPaddingTopRatio={
              passRateHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingTopRatio
            }
            yDomainPaddingBottomRatio={
              passRateHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingBottomRatio
            }
          />
        ) : (
          renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
        ),
      )}
      {metricChart(
        showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: miniPanels.headerHeight }} />
        ) : loading ? (
          renderMiniMetricHeaderLoadingState()
        ) : (
          <MiniMetricChartHeader
            title={getChartTitle('frequency')}
            summary={hasMetricChartValue.frequency ? model.frequencySummary : null}
          />
        ),
        showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: miniPanels.chartHeight }} />
        ) : loading ? (
          renderMiniMetricLoadingState(miniPanels.chartHeight)
        ) : model.showChartErrorState ? (
          chartErrorNode
        ) : model.frequencyMiniChartData && hasMetricChartValue.frequency ? (
          <MiniMetricLineChart
            data={model.frequencyMiniChartData}
            metricKey="frequency"
            color={uiTokens.statusColors.pink}
            height={miniPanels.chartHeight}
            marginLeft={miniPanels.chartMargins.left}
            marginRight={miniPanels.chartMargins.right}
            marginTop={miniPanels.chartMargins.top}
            marginBottom={miniPanels.chartMargins.bottom}
            yAxisWidth={miniPanels.yAxisWidth}
            axisStrokeWidth={miniPanels.axisStrokeWidth}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            yDomainPaddingTopRatio={
              frequencyHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingTopRatio
            }
            yDomainPaddingBottomRatio={
              frequencyHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingBottomRatio
            }
          />
        ) : (
          renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
        ),
      )}
      {metricChart(
        showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: miniPanels.headerHeight }} />
        ) : loading ? (
          renderMiniMetricHeaderLoadingState()
        ) : (
          <MiniMetricChartHeader
            title={getChartTitle('avgDuration')}
            summary={hasMetricChartValue.duration ? model.durationSummary : null}
          />
        ),
        showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: miniPanels.chartHeight }} />
        ) : loading ? (
          renderMiniMetricLoadingState(miniPanels.chartHeight)
        ) : model.showChartErrorState ? (
          chartErrorNode
        ) : model.durationMiniChartData && hasMetricChartValue.duration ? (
          <MiniMetricLineChart
            data={model.durationMiniChartData}
            metricKey="duration"
            color={uiTokens.statusColors.purple}
            height={miniPanels.chartHeight}
            marginLeft={miniPanels.chartMargins.left}
            marginRight={miniPanels.chartMargins.right}
            marginTop={miniPanels.chartMargins.top}
            marginBottom={miniPanels.chartMargins.bottom}
            yAxisWidth={miniPanels.yAxisWidth}
            axisStrokeWidth={miniPanels.axisStrokeWidth}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            yDomainPaddingTopRatio={
              durationHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingTopRatio
            }
            yDomainPaddingBottomRatio={
              durationHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingBottomRatio
            }
          />
        ) : (
          renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
        ),
      )}
      {showFlakeScore
        ? metricChart(
            showPlaceholder ? (
              <Box aria-hidden="true" sx={{ height: miniPanels.headerHeight }} />
            ) : loading ? (
              renderMiniMetricHeaderLoadingState()
            ) : (
              <MiniMetricChartHeader
                title={getChartTitle('flakeScore')}
                summary={hasMetricChartValue.flakeScore ? (model.flakeScoreSummary ?? null) : null}
              />
            ),
            showPlaceholder ? (
              <Box aria-hidden="true" sx={{ height: miniPanels.chartHeight }} />
            ) : loading ? (
              renderMiniMetricLoadingState(miniPanels.chartHeight)
            ) : model.showChartErrorState ? (
              chartErrorNode
            ) : model.flakeScoreMiniChartData && hasMetricChartValue.flakeScore ? (
              <MiniMetricLineChart
                data={model.flakeScoreMiniChartData}
                metricKey="flakeScore"
                color={uiTokens.statusColors.orange}
                height={miniPanels.chartHeight}
                marginLeft={miniPanels.chartMargins.left}
                marginRight={miniPanels.chartMargins.right}
                marginTop={miniPanels.chartMargins.top}
                marginBottom={miniPanels.chartMargins.bottom}
                yAxisWidth={miniPanels.yAxisWidth}
                axisStrokeWidth={miniPanels.axisStrokeWidth}
                hideXAxisLabels
                hideYAxisLabels
                hideXAxisTicks
                hideYAxisTicks
                yDomainPaddingTopRatio={
                  flakeScoreHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingTopRatio
                }
                yDomainPaddingBottomRatio={
                  flakeScoreHasSinglePoint ? 0.2 : miniPanels.yDomainPaddingBottomRatio
                }
              />
            ) : (
              renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
            ),
          )
        : null}
    </Box>
  );
};
